<template>
  <div class="min-h-screen bg-gray-100 pb-20">
    <div class="max-w-[600px] mx-auto">
      
      <!-- Header -->
      <div class="bg-gradient-to-r from-takas-sari to-yellow-400 p-4 sticky top-0 z-10">
        <div class="flex items-center gap-3">
          <button @click="$router.back()" class="p-1">
            <svg class="w-6 h-6 text-takas-koyu" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <div class="flex-1">
            <h1 class="text-xl font-bold text-takas-koyu">{{ categoryName }}</h1>
            <p class="text-xs text-takas-koyu/80">{{ filteredListings.length }} ilan bulundu</p>
          </div>
        </div>
      </div>

      <!-- Arama ve Filtre -->
      <div class="p-4 bg-white shadow-sm sticky top-[72px] z-10">
        <div class="relative mb-3">
          <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="İlanlarda ara..."
            class="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl focus:outline-none focus:border-takas-sari focus:ring-2 focus:ring-takas-sari/20 transition"
          />
        </div>
        
        <!-- Filtre Butonları -->
        <div class="flex gap-2 overflow-x-auto scrollbar-hide">
          <button 
            @click="sortBy = 'newest'"
            :class="[
              'px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition',
              sortBy === 'newest' ? 'bg-takas-sari text-takas-koyu font-medium' : 'bg-gray-100 text-gray-600'
            ]"
          >
            En Yeni
          </button>
          <button 
            @click="sortBy = 'price'"
            :class="[
              'px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition',
              sortBy === 'price' ? 'bg-takas-sari text-takas-koyu font-medium' : 'bg-gray-100 text-gray-600'
            ]"
          >
            Fiyata Göre
          </button>
          <button 
            @click="sortBy = 'popular'"
            :class="[
              'px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition',
              sortBy === 'popular' ? 'bg-takas-sari text-takas-koyu font-medium' : 'bg-gray-100 text-gray-600'
            ]"
          >
            En Popüler
          </button>
          <button 
            @click="showFilters = !showFilters"
            :class="[
              'px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition',
              showFilters ? 'bg-takas-sari text-takas-koyu font-medium' : 'bg-gray-100 text-gray-600'
            ]"
          >
            🔽 Filtreler
          </button>
        </div>

        <!-- Gelişmiş Filtreler -->
        <div v-if="showFilters" class="mt-3 pt-3 border-t border-gray-200">
          <div class="grid grid-cols-2 gap-3">
            <select v-model="priceRange" class="p-2 bg-gray-100 rounded-lg text-sm">
              <option value="all">Tüm Fiyatlar</option>
              <option value="0-100">0 - 100 TL</option>
              <option value="100-500">100 - 500 TL</option>
              <option value="500-1000">500 - 1000 TL</option>
              <option value="1000+">1000 TL ve üzeri</option>
            </select>
            <select v-model="condition" class="p-2 bg-gray-100 rounded-lg text-sm">
              <option value="all">Tüm Durumlar</option>
              <option value="new">Sıfır</option>
              <option value="like-new">Az Kullanılmış</option>
              <option value="good">İyi</option>
              <option value="fair">Orta</option>
            </select>
          </div>
          <div class="mt-3">
            <label class="text-sm text-gray-600 block mb-1">Konum</label>
            <input v-model="location" type="text" placeholder="İl/İlçe" class="w-full p-2 bg-gray-100 rounded-lg text-sm">
          </div>
          <button @click="resetFilters" class="mt-3 text-xs text-takas-sari font-medium">Filtreleri Temizle</button>
        </div>
      </div>

      <!-- Vitrin İlanları (Kategori Özel) -->
      <div class="p-4" v-if="featuredListings.length">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-md font-bold text-gray-800">🌟 Bu Kategoride Öne Çıkanlar</h2>
        </div>
        <div class="overflow-x-auto scrollbar-hide">
          <div class="flex gap-3">
            <div v-for="listing in featuredListings" :key="listing.id" 
                 @click="goToDetail(listing)"
                 class="flex-shrink-0 w-36 bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition">
              <div class="h-28 bg-gradient-to-br from-takas-sari/30 to-yellow-400/30 flex items-center justify-center">
                <span class="text-3xl">{{ listing.icon }}</span>
              </div>
              <div class="p-2">
                <h3 class="font-semibold text-xs text-gray-800 truncate">{{ listing.title }}</h3>
                <p class="text-[10px] text-takas-sari font-bold mt-1">{{ listing.price || 'Takaslık' }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- İlan Listesi -->
      <div class="p-4 pt-0">
        <div v-if="filteredListings.length === 0" class="text-center py-12">
          <div class="text-gray-400 mb-3">
            <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-md font-medium text-gray-700">Bu kategoride ilan bulunamadı</h3>
          <p class="text-xs text-gray-500 mt-1">İlk ilanı sen ver!</p>
          <button @click="goToAddListing" class="mt-4 bg-takas-sari text-takas-koyu px-4 py-2 rounded-lg text-sm font-medium">
            İlan Ver
          </button>
        </div>

        <div v-else class="space-y-3">
          <div v-for="listing in filteredListings" :key="listing.id" 
               @click="goToDetail(listing)"
               class="bg-white rounded-xl p-3 flex gap-3 cursor-pointer hover:shadow-md transition">
            <div class="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <span class="text-3xl">{{ listing.icon }}</span>
            </div>
            <div class="flex-1">
              <div class="flex justify-between items-start">
                <h3 class="font-semibold text-gray-800">{{ listing.title }}</h3>
                <span class="text-xs text-takas-sari font-bold">{{ listing.price || 'Takaslık' }}</span>
              </div>
              <p class="text-xs text-gray-500 mt-1 line-clamp-2">{{ listing.description }}</p>
              <div class="flex items-center gap-3 mt-2">
                <div class="flex items-center gap-1">
                  <svg class="w-3 h-3 text-takas-sari" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span class="text-xs text-gray-600">{{ listing.rating || 'Yeni' }}</span>
                </div>
                <span class="text-xs text-gray-400">{{ listing.location || 'Belirtilmemiş' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Yükleme Butonu -->
      <div v-if="hasMore" class="text-center pb-4">
        <button @click="loadMore" class="text-takas-sari text-sm font-medium">Daha Fazla Yükle ↓</button>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'CategoryDetail',
  data() {
    return {
      categoryName: '',
      searchQuery: '',
      sortBy: 'newest',
      showFilters: false,
      priceRange: 'all',
      condition: 'all',
      location: '',
      visibleCount: 10,
      allListings: [
        { id: 1, title: 'iPhone 14 Pro', description: 'Kutusuyla birlikte, hiç çizik yok', icon: '📱', price: 'Takaslık', rating: '4.8', location: 'İstanbul', category: 'Telefon', isFeatured: true, date: '2024-01-15', views: 234 },
        { id: 2, title: 'MacBook Air M2', description: '8GB RAM, 256GB SSD, garantili', icon: '💻', price: 'Takas+5000₺', rating: '4.9', location: 'Ankara', category: 'Bilgisayar', isFeatured: true, date: '2024-01-10', views: 189 },
        { id: 3, title: 'PlayStation 5', description: '2 kumanda, 3 oyun hediye', icon: '🎮', price: 'Takaslık', rating: '5.0', location: 'İzmir', category: 'Oyun & Konsol', isFeatured: true, date: '2024-01-12', views: 312 },
        { id: 4, title: 'Samsung 65" TV', description: '4K UHD, Smart TV', icon: '📺', price: 'Takas+3000₺', rating: '4.7', location: 'Bursa', category: 'Elektronik', isFeatured: false, date: '2024-01-08', views: 145 },
        { id: 5, title: 'İngilizce Kursu', description: '20 saat online ders', icon: '📚', price: 'Takaslık', rating: '4.6', location: 'Online', category: 'Hizmet', isFeatured: false, date: '2024-01-14', views: 67 },
        { id: 6, title: 'Samsung S23 Ultra', description: 'Kutusu ve aksesuarlarıyla', icon: '📱', price: 'Takas+7000₺', rating: '4.9', location: 'Antalya', category: 'Telefon', isFeatured: false, date: '2024-01-09', views: 201 },
        { id: 7, title: 'Gaming Bilgisayar', description: 'RTX 3060, i7 12.nesil', icon: '💻', price: 'Takaslık', rating: '4.8', location: 'İstanbul', category: 'Bilgisayar', isFeatured: true, date: '2024-01-11', views: 278 },
        { id: 8, title: 'Nintendo Switch', description: 'OLED model, 2 oyun', icon: '🎮', price: 'Takas+2000₺', rating: '4.7', location: 'Ankara', category: 'Oyun & Konsol', isFeatured: false, date: '2024-01-07', views: 156 }
      ]
    }
  },
  computed: {
    categoryListings() {
      return this.allListings.filter(item => item.category === this.categoryName)
    },
    featuredListings() {
      return this.categoryListings.filter(item => item.isFeatured).slice(0, 5)
    },
    filteredListings() {
      let result = [...this.categoryListings]
      
      // Arama filtresi
      if (this.searchQuery) {
        result = result.filter(item => 
          item.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          item.description.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }
      
      // Fiyat filtresi
      if (this.priceRange !== 'all') {
        // Fiyat filtreleme mantığı
      }
      
      // Konum filtresi
      if (this.location) {
        result = result.filter(item => 
          item.location?.toLowerCase().includes(this.location.toLowerCase())
        )
      }
      
      // Sıralama
      if (this.sortBy === 'newest') {
        result.sort((a, b) => new Date(b.date) - new Date(a.date))
      } else if (this.sortBy === 'popular') {
        result.sort((a, b) => b.views - a.views)
      }
      
      return result.slice(0, this.visibleCount)
    },
    hasMore() {
      return this.visibleCount < this.categoryListings.length
    }
  },
  created() {
    this.categoryName = this.$route.params.categoryName
  },
  methods: {
    loadMore() {
      this.visibleCount += 10
    },
    resetFilters() {
      this.priceRange = 'all'
      this.condition = 'all'
      this.location = ''
      this.searchQuery = ''
    },
    goToDetail(listing) {
      this.$router.push(`/ilan/${listing.id}`)
    },
    goToAddListing() {
      this.$router.push('/ilan-ver')
    }
  }
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>